// India-first financial calculators. All amounts in INR.
const pow = Math.pow;

export function sip({ monthly, rate, years, stepUp = 0 }) {
  const i = rate / 1200;
  let invested = 0, fv = 0, m = monthly;
  const points = [{ year: 0, invested: 0, value: 0 }];
  for (let y = 1; y <= years; y++) {
    for (let k = 0; k < 12; k++) {
      fv = (fv + m) * (1 + i);
      invested += m;
    }
    m *= 1 + stepUp / 100;
    points.push({ year: y, invested: Math.round(invested), value: Math.round(fv) });
  }
  return { invested, futureValue: fv, gains: fv - invested, points };
}

export function lumpsum({ principal, rate, years }) {
  const points = [];
  for (let y = 0; y <= years; y++) points.push({ year: y, invested: principal, value: Math.round(principal * pow(1 + rate / 100, y)) });
  const fv = principal * pow(1 + rate / 100, years);
  return { invested: principal, futureValue: fv, gains: fv - principal, points };
}

export function goalPlanner({ goalToday, years, rate, inflation = 0, currentSavings = 0 }) {
  const targetFuture = goalToday * pow(1 + inflation / 100, years);
  const i = rate / 1200, m = years * 12;
  const fvSavings = currentSavings * pow(1 + rate / 100, years);
  const need = Math.max(0, targetFuture - fvSavings);
  const factor = ((pow(1 + i, m) - 1) / i) * (1 + i);
  const requiredSip = need / factor;
  const requiredLumpsum = need / pow(1 + rate / 100, years);
  return { targetFuture, fvSavings, need, requiredSip, requiredLumpsum };
}

export function homeLoan({ principal, rate, years, prepayMonthly = 0, prepayAnnual = 0, oneTime = 0, oneTimeMonth = 0 }) {
  const i = rate / 1200, n = Math.round(years * 12);
  const emi = i === 0 ? principal / n : (principal * i * pow(1 + i, n)) / (pow(1 + i, n) - 1);
  let bal = principal, totalInterest = 0, month = 0;
  const rows = [];
  while (bal > 0 && month < n + 720) {
    month++;
    const interest = bal * i;
    let principalPaid = emi - interest;
    let extra = prepayMonthly + (month % 12 === 0 ? prepayAnnual : 0) + (month === oneTimeMonth ? oneTime : 0);
    if (principalPaid + extra > bal) { extra = Math.max(0, bal - principalPaid); principalPaid = Math.min(principalPaid, bal); }
    bal -= principalPaid + extra;
    if (bal < 1) bal = 0;
    totalInterest += interest;
    rows.push({ month, interest, principal: principalPaid + extra, balance: bal });
    if (bal <= 0) break;
  }
  const yearly = [];
  for (let y = 1; y <= Math.ceil(month / 12); y++) {
    const slice = rows.slice((y - 1) * 12, y * 12);
    if (!slice.length) break;
    yearly.push({
      year: y,
      interest: Math.round(slice.reduce((s, r) => s + r.interest, 0)),
      principal: Math.round(slice.reduce((s, r) => s + r.principal, 0)),
      balance: Math.round(slice[slice.length - 1].balance),
    });
  }
  const baseInterest = emi * n - principal;
  return {
    emi, totalInterest, totalPayment: principal + totalInterest, months: month, rows, yearly,
    baseInterest, interestSaved: baseInterest - totalInterest, monthsSaved: n - month,
    taxBenefitYr: Math.min(200000, rows.slice(0, 12).reduce((s, r) => s + r.interest, 0)),
  };
}

export function balanceAtYear(hl, y) {
  const idx = y * 12 - 1;
  return idx < hl.rows.length ? hl.rows[idx].balance : 0;
}

export function fd({ principal, rate, years, compounding = "quarterly", payout = "cumulative", senior = false }) {
  const r = (rate + (senior ? 0.5 : 0)) / 100;
  const nMap = { monthly: 12, quarterly: 4, halfyearly: 2, yearly: 1 };
  if (payout === "cumulative") {
    const n = nMap[compounding] || 4;
    const points = [];
    for (let y = 0; y <= years; y++) points.push({ year: y, value: Math.round(principal * pow(1 + r / n, n * y)) });
    const maturity = principal * pow(1 + r / n, n * years);
    return { maturity, interest: maturity - principal, principal, effectiveRate: (pow(1 + r / n, n) - 1) * 100, payout, rate: rate + (senior ? 0.5 : 0), points };
  }
  const freq = nMap[payout] || 4;
  const periodicInterest = (principal * r) / freq;
  const totalInterest = periodicInterest * freq * years;
  return { maturity: principal, interest: totalInterest, principal, periodicInterest, freq, payout, rate: rate + (senior ? 0.5 : 0) };
}

export function ppf({ yearlyDeposit, rate = 7.1, years = 15 }) {
  let bal = 0, invested = 0;
  const rows = [];
  for (let y = 1; y <= years; y++) {
    bal += yearlyDeposit; invested += yearlyDeposit;
    bal *= 1 + rate / 100;
    rows.push({ year: y, invested: Math.round(invested), value: Math.round(bal) });
  }
  return { maturity: bal, invested, interest: bal - invested, rows, rate };
}

export function nps({ monthly, rate, currentAge, annuityPct = 40, annuityReturn = 6, stepUp = 0 }) {
  const years = Math.max(1, 60 - currentAge);
  const s = sip({ monthly, rate, years, stepUp });
  const corpus = s.futureValue;
  const lumpSum = corpus * (1 - annuityPct / 100);
  const annuityCorpus = corpus * (annuityPct / 100);
  const monthlyPension = (annuityCorpus * (annuityReturn / 100)) / 12;
  return { corpus, invested: s.invested, gains: s.gains, lumpSum, annuityCorpus, monthlyPension, years, points: s.points };
}

export function rentalYield({ propertyValue, monthlyRent, annualExpenses = 0, annualAppreciation = 0 }) {
  const annualRent = monthlyRent * 12;
  const gross = propertyValue > 0 ? (annualRent / propertyValue) * 100 : 0;
  const net = propertyValue > 0 ? ((annualRent - annualExpenses) / propertyValue) * 100 : 0;
  return { annualRent, gross, net, totalReturn: net + annualAppreciation };
}

export function termInsurance({ age, retireAge = 60, monthlyExpenses, annualIncome, liabilities = 0, existingCover = 0, existingSavings = 0, inflation = 6, method = "expense" }) {
  const yearsLeft = Math.max(1, retireAge - age);
  let need = 0;
  if (method === "expense") {
    let exp = monthlyExpenses * 12;
    for (let y = 0; y < yearsLeft; y++) { need += exp; exp *= 1 + inflation / 100; }
  } else {
    need = annualIncome * 12;
  }
  const recommended = Math.max(0, need + liabilities - existingCover - existingSavings);
  return { need, recommended, yearsLeft };
}

export function healthCover({ cityTier = "tier1", familyMembers = 2, eldestAge = 40, existingCover = 0 }) {
  const base = cityTier === "tier1" ? 1000000 : cityTier === "tier2" ? 750000 : 500000;
  let cover = base + Math.max(0, familyMembers - 1) * 250000;
  if (eldestAge > 45) cover += 500000;
  if (eldestAge > 60) cover += 500000;
  return { recommended: cover, topUpNeeded: Math.max(0, cover - existingCover), base };
}

export function buyVsRent({ propertyPrice, downPayment, loanRate, loanYears, appreciation, monthlyRent, rentInflation, investReturn, horizon, maintenancePct = 1 }) {
  const loan = Math.max(0, propertyPrice - downPayment);
  const hl = homeLoan({ principal: loan, rate: loanRate, years: loanYears });
  let renter = downPayment;
  let rentYr = monthlyRent * 12;
  let homeValue = propertyPrice;
  const points = [];
  for (let y = 1; y <= horizon; y++) {
    const emiAnnual = y <= loanYears ? hl.emi * 12 : 0;
    const maint = homeValue * (maintenancePct / 100);
    const buyerCost = emiAnnual + maint;
    const diff = buyerCost - rentYr;
    renter = renter * (1 + investReturn / 100) + Math.max(0, diff) * (1 + investReturn / 200);
    homeValue *= 1 + appreciation / 100;
    rentYr *= 1 + rentInflation / 100;
    const buyerNet = homeValue - balanceAtYear(hl, y);
    points.push({ year: y, buyer: Math.round(buyerNet), renter: Math.round(renter) });
  }
  const last = points[points.length - 1] || { buyer: 0, renter: 0 };
  return { emi: hl.emi, points, buyerNet: last.buyer, renterNet: last.renter, verdict: last.buyer >= last.renter ? "buy" : "rent" };
}

const NEW_SLABS = [[400000, 0], [800000, 0.05], [1200000, 0.10], [1600000, 0.15], [2000000, 0.20], [2400000, 0.25], [Infinity, 0.30]];
const OLD_SLABS = [[250000, 0], [500000, 0.05], [1000000, 0.20], [Infinity, 0.30]];

function slabTax(income, slabs) {
  let tax = 0, prev = 0;
  for (const [limit, rate] of slabs) {
    if (income > prev) { tax += (Math.min(income, limit) - prev) * rate; prev = limit; } else break;
  }
  return tax;
}

export function incomeTax({ grossIncome, ded80C = 0, ded80D = 0, hra = 0, homeLoanInterest = 0, nps80CCD = 0, otherDed = 0, isSalaried = true }) {
  const stdNew = isSalaried ? 75000 : 0;
  const taxableNew = Math.max(0, grossIncome - stdNew);
  let taxNew = taxableNew <= 1200000 ? 0 : slabTax(taxableNew, NEW_SLABS);
  const cessNew = taxNew * 0.04;
  taxNew += cessNew;

  const stdOld = isSalaried ? 50000 : 0;
  const totalDed = stdOld + Math.min(ded80C, 150000) + ded80D + hra + Math.min(homeLoanInterest, 200000) + Math.min(nps80CCD, 50000) + otherDed;
  const taxableOld = Math.max(0, grossIncome - totalDed);
  let taxOld = taxableOld <= 500000 ? 0 : slabTax(taxableOld, OLD_SLABS);
  taxOld += taxOld * 0.04;

  return {
    taxNew: Math.round(taxNew), taxOld: Math.round(taxOld),
    taxableNew, taxableOld: Math.round(taxableOld), totalDedOld: totalDed,
    better: taxNew <= taxOld ? "new" : "old", savings: Math.round(Math.abs(taxNew - taxOld)),
  };
}


// ------------------------- Emergency Fund -------------------------
export const STABILITY_GUIDE = {
  stable: { label: "Stable (salaried)", range: "3–6 months", min: 3, max: 6 },
  variable: { label: "Variable income", range: "6–9 months", min: 6, max: 9 },
  single: { label: "Single income / risky", range: "9–12 months", min: 9, max: 12 },
};

export function emergencyFund({ essential = 0, nonEssential = 0, includeNonEssential = false, months = 6, existingFund = 0, stability = "stable" }) {
  const monthlyNeed = essential + (includeNonEssential ? nonEssential : 0);
  const target = monthlyNeed * months;
  const gap = Math.max(0, target - existingFund);
  const guide = STABILITY_GUIDE[stability] || STABILITY_GUIDE.stable;
  return { monthlyNeed, months, target, gap, covered: existingFund >= target, guide };
}

// ------------------------- Education Planner -------------------------
export function educationPlanner({ currentCost, years, inflation = 10, rate = 12, currentSavings = 0 }) {
  const futureCost = currentCost * pow(1 + inflation / 100, years);
  const fvSavings = currentSavings * pow(1 + rate / 100, years);
  const need = Math.max(0, futureCost - fvSavings);
  const i = rate / 1200, m = years * 12;
  const factor = ((pow(1 + i, m) - 1) / i) * (1 + i);
  const requiredSip = need / factor;
  const requiredLumpsum = need / pow(1 + rate / 100, years);
  const points = [];
  for (let y = 0; y <= years; y++) points.push({ year: y, value: Math.round(currentCost * pow(1 + inflation / 100, y)) });
  return { futureCost, fvSavings, need, requiredSip, requiredLumpsum, points };
}

// ------------------------- Capital Gains (FY 2025-26) -------------------------
export const CII = {
  "2001-02": 100, "2002-03": 105, "2003-04": 109, "2004-05": 113, "2005-06": 117,
  "2006-07": 122, "2007-08": 129, "2008-09": 137, "2009-10": 148, "2010-11": 167,
  "2011-12": 184, "2012-13": 200, "2013-14": 220, "2014-15": 240, "2015-16": 254,
  "2016-17": 264, "2017-18": 272, "2018-19": 280, "2019-20": 289, "2020-21": 301,
  "2021-22": 317, "2022-23": 331, "2023-24": 348, "2024-25": 363, "2025-26": 376,
};

export function capitalGains({ assetType, buyValue, sellValue, expenses = 0, term, purchaseFY = "2020-21", slabRate = 30 }) {
  const rawGain = sellValue - buyValue - expenses;
  const gain = Math.max(0, rawGain);
  let taxableGain = gain, taxRate = 0, method = "", exemption = 0, tax = 0, indexedCost = 0;

  if (assetType === "equity") {
    if (term === "long") {
      exemption = Math.min(gain, 125000);
      taxableGain = Math.max(0, gain - 125000);
      taxRate = 12.5;
      method = "LTCG 12.5% on gains above ₹1.25L (no indexation)";
      tax = taxableGain * 0.125;
    } else {
      taxRate = 20;
      method = "STCG 20% (Section 111A)";
      tax = gain * 0.20;
    }
  } else {
    if (term === "long") {
      const taxA = gain * 0.125;
      const cS = CII["2025-26"];
      const cP = CII[purchaseFY] || cS;
      indexedCost = (buyValue + expenses) * (cS / cP);
      const indexedGain = Math.max(0, sellValue - indexedCost);
      const taxB = indexedGain * 0.20;
      const eligibleIndex = parseInt(purchaseFY, 10) <= 2024;
      if (eligibleIndex && taxB < taxA) {
        tax = taxB; taxRate = 20; method = "20% with indexation (grandfathered)"; taxableGain = indexedGain;
      } else {
        tax = taxA; taxRate = 12.5; method = "12.5% without indexation"; taxableGain = gain;
      }
    } else {
      taxRate = slabRate;
      method = `STCG taxed at your slab (${slabRate}%)`;
      tax = gain * (slabRate / 100);
    }
  }
  const cess = tax * 0.04;
  const total = tax + cess;
  return {
    gain, taxableGain, exemption, taxRate, method,
    tax: Math.round(tax), cess: Math.round(cess), total: Math.round(total),
    indexedCost: Math.round(indexedCost), netProfit: Math.round(gain - total),
  };
}
